import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { CircularProgress, Box, useTheme } from '@material-ui/core';
import produce from 'immer';
import clsx from 'clsx';
import { modeUIContext, setIsEditingForm, showSuccessNotification } from '@moderepo/mode-ui-state';
import { ConfigParam, ConfigParamGroup, ConfigParamType, GatewayDefinition, StringConfigParam, NumberConfigParam, GatewayConfigData }
    from '@moderepo/mode-apis';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { capitalizeAll, capitalizeFirst, GenericMessageBox } from '@moderepo/mode-app-components';
import { createDeviceKVStore, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { isConfigParamHidden } from '@moderepo/mode-app-components-internal';
import { useGetProjectGatewayCatalog, useRitzTranslation } from '../../utils/customHooks';
import { GenericConfigForm } from '../../components/robot-cloud';
import { GenericConfigFormProps, GenericConfigFormUpdatableInputs } from '../../components/robot-cloud/GenericConfigForm';
import { RCGatewayConfig, useGetRCGatewayConfig } from './RCUtils';



export interface RCGatewayConfigContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly gatewayId: number;
    readonly gatewayConfigKey: string;          // The KV store key where the device config is stored. Will be undefined if this device config is new

    readonly newGatewayConfigSettings?: {        // Only available if this container is used for creating new device config
        readonly onNewGatewayConfigCreated: (configData: GatewayConfigData)=> void;
        readonly onNewGatewayConfigCanceled: ()=> void;
    }
}


/**
 * RCGatewayConfigContainer is the wrapper for the Gateway config component. It provides all the logic and the Gateway config component doesn't need
 * to do anything other than displaying data.
 * @param props
 */
export const RCGatewayConfigContainer = (props: RCGatewayConfigContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const selectedDevice = selectProjectDeviceById(dataState, props.gatewayId);
    const gatewayCatalog = useGetProjectGatewayCatalog(project);
    const theme = useTheme();


    // Find the Gateway definition for the selected device. If it exist then the selected device is a gateway
    const selectedGatewayDefinition = useMemo(() => {
        if (selectedDevice && gatewayCatalog) {
            return gatewayCatalog.find((gatewayDef: GatewayDefinition) => {
                return gatewayDef.deviceClass === selectedDevice.deviceClass;
            });
        }
        return undefined;
    }, [gatewayCatalog, selectedDevice]);

    // Check if the selected device is a gateway
    const isGateway = useMemo(() => {
        return selectedGatewayDefinition !== undefined;
    }, [selectedGatewayDefinition]);


    // Get the configParams schema from the selectedGatewayDefinition if it exist
    const selectedGatewayConfigSchema = useMemo((): readonly ConfigParamGroup[] | undefined => {
        return selectedGatewayDefinition?.configParams.paramGroups;
    }, [selectedGatewayDefinition]);


    // A state that indicate whether or not we are in create NEW config mode
    const [isCreateConfig, setIsCreateConfig] = useState<boolean>(false);


    // Get the Gateway config
    const selectedGatewayConfig = useGetRCGatewayConfig(props.gatewayId);


    // Get the Gateway config gateway settings. The settings is stored under 'parameterGroups' attribute
    const selectedGatewayConfigData = useMemo((): GatewayConfigData | undefined => {
        return selectedGatewayConfig ? selectedGatewayConfig.parameterGroups : {};
    }, [selectedGatewayConfig]);



    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);


    /**
     * Because this container can be used to display ALL type of devices or only Gateways therefore the translations will depend on whether we are
     * displaying all or gatewaysOnly. If we are displaying all type of devices then all the text/labels/messages will use the term "device"
     * for devices. If we are showing only gateways then instead of using the term "device", we will show a more specific term, "gateway".
     * This map DOES NOT include all the text we use on this page, only the text that has the word "device" in it.
     */
    const labels = useMemo((): {
        readonly panelTitle: string;
        readonly emptyConfigMessageTitle: string;
        readonly configureDevice: string;
    } => {
        if (isGateway) {
            return {
                panelTitle             : capitalizeAll(trans('label.gateway_config')),
                emptyConfigMessageTitle: capitalizeFirst(trans('generic.no_data.title', { term: trans('terms.gateway', { count: 2 }) })),
                configureDevice        : capitalizeAll(trans('label.configure_gateway')),
            };
        }

        return {
            panelTitle             : capitalizeAll(trans('label.device_config')),
            emptyConfigMessageTitle: capitalizeFirst(trans('generic.no_data.title', { term: trans('terms.gateway', { count: 2 }) })),
            configureDevice        : capitalizeAll(trans('label.configure_device')),
        };
    }, [isGateway, trans]);


    // Unlike other components, we don't know ahead of time what inputs are required therefore we can't create an interface for the inputError.
    // The inputErrors' structure will be the same as the config schema.
    const [inputErrors, setInputErrors] = useState<{}>({ });


    /**
     * This useEffect is used for loading device data on component init
     */
    useEffect(() => {
        if (!selectedDevice) {
            dataDispatch(fetchProjectDeviceById(props.projectId, props.gatewayId)).catch(() => { /* ignore error */ });
        }
    }, [dataDispatch, props.projectId, props.gatewayId, selectedDevice]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);



    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
            deviceClass: null,
        });

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onConfigFormInputChange = useCallback((groupName: string, fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            if (draft[groupName]) {
                draft[groupName][fieldName] = null;
            }
        }));
    }, [inputErrors]);



    /**
     * Handler for when the user click Save input from the user form
     */
    const onConfigFormSubmit = useCallback(async (inputs: GenericConfigFormUpdatableInputs) => {
        if (!project || !selectedGatewayConfigSchema) {
            return;
        }

        const { values } = inputs;
        const newInputError = {};


        // Go through each config group in the configSchema and valid each config group
        selectedGatewayConfigSchema.forEach((configGroup: ConfigParamGroup) => {
            const groupKey = configGroup.key;

            // Create a new object inside inputErrors for the current configGroup if there isn't one yet
            // We will place error messages in this object when we detect error in any of the config param.
            if (!newInputError[groupKey]) {
                newInputError[groupKey] = {};
            }

            // Go through each config param in the configGroup and valid each config param
            configGroup.params.forEach((configParam: ConfigParam<any>) => {
                const configKey = configParam.key;

                // First, check if configParam is required and the user provided a value for it
                if (configParam.required && (!values[groupKey] || values[groupKey][configKey] === undefined)) {

                    // If configParam is required but there is no input value for the config, it might not be an error just yet. It is possible that
                    // this input depends on other inputs so this input might not be visible. So we must also need to check this config is even
                    // visible or hidden.
                    const isConfigHidden = isConfigParamHidden(configParam, values[groupKey]);
                    if (isConfigHidden) {
                        // this configParam is hidden so it is ok that it does not have a value
                    } else {
                        // this config is not hidden so it a error if it does not have a value
                        newInputError[groupKey][configKey] = capitalizeFirst(trans('error.input.generic.empty'));
                    }
                }

                // If there is no error yet, check if the input value is valid
                if (!newInputError[groupKey][configKey] && values[groupKey] && values[groupKey][configKey] !== undefined) {
                    if (configParam.type === ConfigParamType.STRING) {

                        const actualConfigParam = configParam as StringConfigParam;
                        if (actualConfigParam.regex && !(new RegExp(actualConfigParam.regex)).test(values[groupKey][configKey])) {
                            // validate input base on regex
                            newInputError[groupKey][configKey] = capitalizeFirst(trans('error.input.generic.invalid'));
                        } else if (actualConfigParam.minLength !== undefined && values[groupKey][configKey].length < actualConfigParam.minLength) {
                            // validate input base on minLength
                            newInputError[groupKey][configKey] = capitalizeFirst(trans('error.input.generic.length', {
                                min: actualConfigParam.minLength, max: actualConfigParam.maxLength || '∞',
                            }));
                        } else if (actualConfigParam.maxLength !== undefined && values[groupKey][configKey].length > actualConfigParam.maxLength) {
                            // validate input base on maxLength
                            newInputError[groupKey][configKey] = capitalizeFirst(trans('error.input.generic.length', {
                                min: actualConfigParam.minLength || '0', max: actualConfigParam.maxLength,
                            }));
                        }
                    } else if (configParam.type === ConfigParamType.NUMBER) {

                        const actualConfigParam = configParam as NumberConfigParam;
                        const value = Number((values[groupKey][configKey]));

                        // check if the user input is a number
                        if (Number.isNaN(value)) {
                            newInputError[groupKey][configKey] = capitalizeFirst(trans('error.input.generic.invalid'));
                        } else {
                            if (actualConfigParam.min !== undefined && value < actualConfigParam.min) {
                                // validate input base on min
                                newInputError[groupKey][configKey] = capitalizeFirst(trans('error.input.generic.range', {
                                    min: actualConfigParam.min, max: actualConfigParam.max || '∞',
                                }));
                            }
                            if (actualConfigParam.max !== undefined && value > actualConfigParam.max) {
                                // validate input base on max
                                newInputError[groupKey][configKey] = capitalizeFirst(trans('error.input.generic.range', {
                                    min: actualConfigParam.min || '-∞', max: actualConfigParam.max || '∞',
                                }));
                            }
                        }
                    }

                    // We don't need to validate Boolean because 'undefined' will be treated as false
                    // We also don't need to validate StringEnum or NumberEnum type other than checking if they have a value.
                }
            });

            // If there is no error in the current group, delete the object for that group
            if (Object.keys(newInputError[groupKey]).length <= 0) {
                delete newInputError[groupKey];
            }
        });

        setInputErrors(newInputError);

        const hasErrors = Object.keys(newInputError).length > 0;
        if (!hasErrors && values) {
            // if everything is good, make an API call to create/update the Device config using Device KV store
            try {
                const rcGatewayConfig: RCGatewayConfig = {
                    parameterGroups: values,
                    drivers        : selectedGatewayDefinition ? selectedGatewayDefinition.drivers : undefined,
                };

                appDataDispatch(createDeviceKVStore(props.gatewayId, props.gatewayConfigKey, rcGatewayConfig));
                uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', {
                    term: isGateway ? 'terms.gateway' : 'terms.device',
                }));

                closeEditing();
                setIsCreateConfig(false);
            } catch (error) {
                // ignore error
            }
        }

    }, [project, selectedGatewayConfigSchema, trans, appDataDispatch, props.gatewayId, props.gatewayConfigKey, selectedGatewayDefinition,
        uiDispatch, isGateway, closeEditing]);



    /**
     * On Cancel edit, call closeEditing to clear input errors and edit mode AND if we are trying to create new device, also dispatch an
     * event to the container to let the container know the create device flow is canceled.
     */
    const onCancelEdit = useCallback(() => {
        closeEditing();

        if (isCreateConfig) {
            setIsCreateConfig(false);
        }
    }, [closeEditing, setIsCreateConfig, isCreateConfig]);



    /**
     * Create inputSettings for the GenericConfigForm. Since we don't know the input we will have ahead of time, we can only setup the
     * cancelButton and saveButtons. Everything else will be created dynamically.
     */
    const fieldsSettings = useMemo((): GenericConfigFormProps['fields'] => {
        const defaultSettings = {
        };

        if (project) {
            return {
                ...defaultSettings,
            };
        }

        return defaultSettings;
    }, [project]);


    const onAddConfigClick = useCallback(() => {
        setIsCreateConfig(true);
        onChangeEditingMode(true);
    }, [onChangeEditingMode]);


    // Check if the gateway config has out of date drivers settings. The gateway config has out of dates driver settings if its string
    // representation of the 'drivers' settings is not the same as the string representation of the gateway catalog's drivers settings.
    // Also, we only need to do this check if 'selectedGatewayConfigValue' is not defined. 'selectedGatewayConfigValue' is undefined means
    // The gateway is not even configured.
    const hasOutOfDateDrivers = selectedGatewayConfig
        && JSON.stringify(selectedGatewayConfig.drivers) !== JSON.stringify(selectedGatewayDefinition?.drivers);


    return (
        <>
            {project && gatewayCatalog && gatewayCatalog.length > 0 && (
                <div>
                    {hasOutOfDateDrivers && (
                        <Box padding={1} fontSize="large" color={theme.palette.error.main}>
                            {capitalizeFirst(trans('error.gateway_driver.out_of_date'))}
                        </Box>
                    )}
                    <GenericConfigForm
                        configSchema={selectedGatewayConfigSchema}
                        configData={selectedGatewayConfigData}
                        configErrors={inputErrors}
                        className={clsx(props.className, isEditing && 'editing')}
                        isEditing={isEditing}
                        title={labels.panelTitle}
                        icon="settings"
                        fields={fieldsSettings}
                        noDataMessageComp={(
                            <GenericMessageBox
                                title={labels.emptyConfigMessageTitle}
                                action={labels.configureDevice}
                                actionHandler={onAddConfigClick}
                            />
                        )}
                        actions={selectedGatewayConfigData !== undefined ? {
                            startEdit: {
                                onClick: () => {
                                    onChangeEditingMode(true);
                                },
                            },
                            cancelEdit: {
                                label  : capitalizeAll(trans('label.cancel')),
                                onClick: onCancelEdit,
                            },
                            saveUpdate: {
                                label  : capitalizeAll(trans('label.save')),
                                onClick: onConfigFormSubmit,
                            },
                        } : undefined}
                        onGroupFieldClicked={() => {
                            onChangeEditingMode(true);
                        }}
                        onGroupInputChange={onConfigFormInputChange}
                    />
                </div>
            )}


            {(!project || !gatewayCatalog) && (
                <CircularProgress />
            )}
        </>
    );
};
